import { useEffect, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { StoreButtons } from "@/components/site/StoreButtons";
import { Reveal, MaskReveal } from "@/components/site/Reveal";
import { FeatureIcon } from "@/components/site/FeatureIcon";
import { Compass, ArrowUpRight, Star } from "lucide-react";

const MARQUEE = ["Şelaleler", "Göller", "Yaylalar", "Kanyonlar", "Tarihi Rotalar", "Milli Parklar", "Saklı Koylar"];

const CHAPTERS = [
  { no: "01", title: "Keşfedilmemiş Doğa", text: "Turistik olmayan, yalnızca yerellerin bildiği şelaleler, göller ve yaylalar." },
  { no: "02", title: "Yerel İzler", text: "Her rotanın ardında bir hikâye. Tarihi yerleri ve kültürel dokuları keşfet." },
  { no: "03", title: "Akıllı Teknoloji", text: "Yapay zeka destekli rota oluşturucu ile sana özel keşif planları." },
];

export default function Home() {
  const { settings } = useSite();
  const [features, setFeatures] = useState([]);
  const [home, setHome] = useState({ title: "", body: "" });
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 140]);
  const heroScale = useTransform(scrollY, [0, 600], [1, 1.12]);

  useEffect(() => {
    api.get("/features").then(({ data }) => setFeatures(data)).catch(() => {});
    api.get("/pages/home").then(({ data }) => setHome(data)).catch(() => {});
  }, []);

  const title = home.title || "Türkiye'nin Saklı Kalmış Güzelliklerini Keşfet";
  const titleLines = title.split(" ").reduce((acc, w, i) => {
    const idx = Math.floor(i / 3);
    acc[idx] = (acc[idx] ? acc[idx] + " " : "") + w;
    return acc;
  }, []);

  return (
    <>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-[#0A1A10]">
        <motion.div className="absolute inset-0" style={{ y: heroY, scale: heroScale }}>
          <img
            src={settings.hero_image_url}
            alt="Türkiye'nin saklı doğası"
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A1A10] via-[#0A1A10]/50 to-[#0A1A10]/30" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A1A10]/70 to-transparent" />
        </motion.div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-5 sm:px-8 pt-24 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 mb-7 px-4 py-2 rounded-full border border-[#D4AF37]/30 bg-white/5 backdrop-blur-sm"
          >
            <Compass className="h-4 w-4 text-[#D4AF37]" />
            <span className="font-mono-accent text-xs uppercase tracking-widest text-white/80">Keşfetmeye Hazır Mısın?</span>
          </motion.div>

          <h1 className="font-serif-display text-white text-4xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] max-w-4xl">
            <MaskReveal lines={titleLines} />
          </h1>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9, duration: 0.8 }}
            className="mt-7 max-w-xl text-lg text-white/80 leading-relaxed"
          >
            {home.body || "Saklı Rota ile şelaleleri, gölleri, yaylaları, tarihi yerleri ve keşfedilmeyi bekleyen binlerce destinasyonu keşfet."}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8 }}
          >
            <StoreButtons className="mt-9" size="lg" />
          </motion.div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-[#0F2F1D] py-5 border-y border-[#D4AF37]/15 overflow-hidden">
        <div className="sr-marquee-track">
          {[...MARQUEE, ...MARQUEE].map((m, i) => (
            <span key={i} className="flex items-center gap-6 px-6 text-white/85 font-serif-display text-2xl italic whitespace-nowrap">
              {m} <Star className="h-4 w-4 text-[#D4AF37] fill-current" />
            </span>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="bg-[#F8F9FA] py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <span className="font-mono-accent text-xs uppercase tracking-widest text-[#40916C]">Manifesto</span>
            <h2 className="font-serif-display text-3xl sm:text-5xl font-bold text-[#0F2F1D] mt-3 max-w-2xl leading-tight">
              Kalabalıktan uzak, doğaya yakın bir keşif felsefesi.
            </h2>
          </Reveal>
          <div className="mt-16 grid gap-px bg-[#E2E8E4] md:grid-cols-3 rounded-2xl overflow-hidden border border-[#E2E8E4]">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.no} delay={i * 0.1}>
                <div className="bg-white p-8 sm:p-10 h-full group hover:bg-[#0F2F1D] transition-colors duration-500">
                  <span className="font-mono-accent text-5xl font-bold text-[#D4AF37]/60 group-hover:text-[#D4AF37] transition-colors">{c.no}</span>
                  <h3 className="font-serif-display text-2xl font-semibold text-[#1A251E] mt-6 group-hover:text-white transition-colors">{c.title}</h3>
                  <p className="mt-3 text-[#5C6F62] leading-relaxed group-hover:text-white/70 transition-colors">{c.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section className="bg-white py-24 sm:py-32" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal className="max-w-2xl">
            <span className="font-mono-accent text-xs uppercase tracking-widest text-[#40916C]">Özellikler</span>
            <h2 className="font-serif-display text-3xl sm:text-5xl font-bold text-[#0F2F1D] mt-3 leading-tight">
              Keşfin her adımı için tasarlandı.
            </h2>
          </Reveal>

          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f, i) => (
              <Reveal key={f.id} delay={(i % 3) * 0.08}>
                <div
                  data-testid={`feature-card-${i}`}
                  className={`group relative h-full rounded-3xl border border-[#E2E8E4] p-8 transition-all duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-[#0F2F1D]/10 ${
                    i === 0 ? "bg-[#0F2F1D] text-white sm:col-span-2 lg:col-span-1" : "bg-[#F8F9FA]"
                  }`}
                >
                  <div className={`grid place-items-center h-14 w-14 rounded-2xl mb-6 transition-transform group-hover:scale-110 ${
                    i === 0 ? "bg-[#D4AF37] text-[#0F2F1D]" : "bg-[#1B4D2E] text-[#D4AF37]"
                  }`}>
                    <FeatureIcon name={f.icon} className="h-7 w-7" />
                  </div>
                  <h3 className={`font-serif-display text-2xl font-semibold ${i === 0 ? "text-white" : "text-[#1A251E]"}`}>{f.title}</h3>
                  <p className={`mt-3 leading-relaxed ${i === 0 ? "text-white/70" : "text-[#5C6F62]"}`}>{f.description}</p>
                  <span className="absolute top-8 right-8 font-mono-accent text-xs opacity-40">0{i + 1}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PREMIUM CTA */}
      <section className="bg-[#F8F9FA] py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2rem] bg-[#0F2F1D] sr-grain px-8 py-14 sm:px-16 sm:py-20">
              <div className="relative max-w-2xl">
                <span className="font-mono-accent text-xs uppercase tracking-widest text-[#D4AF37]">Saklı Rota Premium</span>
                <h2 className="font-serif-display text-3xl sm:text-5xl font-bold text-white mt-4 leading-tight">
                  Sınırsız keşfin kapılarını arala.
                </h2>
                <p className="mt-5 text-white/70 text-lg">
                  Offline haritalar, gizli destinasyonlar ve reklamsız deneyimle Saklı Rota'nın tüm gücünü kullan.
                </p>
                <Link
                  to="/premium"
                  data-testid="home-premium-cta"
                  className="mt-8 inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#D4AF37] text-[#0F2F1D] font-semibold hover:bg-[#C59B27] transition-colors"
                >
                  Premium'u Keşfet <ArrowUpRight className="h-5 w-5" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
